import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import { customTableStyles } from "../../assets/CustomTableStyles";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function FeeCycle() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");

  const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      id: 0,
      name: "",
      interval: "",
      createdBy: localStorage.getItem("userId"),
      organizationId: organizationId,
    },
  });

  const [feeCycles, setFeeCycles] = useState([]);

  useEffect(() => {
    const fetchFeeCycles = async () => {
      try {
        const response = await get(
          `FeeCycle/GetFeeCycleByOrganizationIdAsync?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        setFeeCycles(response.feeCycle);
      } catch (error) {
        console.error("Error fetching feeCycle:", error);
      }
    };

    fetchFeeCycles();
  }, [triggerRerender]);

  const intervals = [
    {
      id: 1,
      name: "1",
    },
    {
      id: 2,
      name: "2",
    },
    {
      id: 3,
      name: "3",
    },
    {
      id: 4,
      name: "4",
    },
    {
      id: 5,
      name: "5",
    },
    {
      id: 6,
      name: "6",
    },
    {
      id: 7,
      name: "7",
    },
    {
      id: 8,
      name: "8",
    },
    {
      id: 9,
      name: "9",
    },
    {
      id: 10,
      name: "10",
    },
    {
      id: 11,
      name: "11",
    },
    {
      id: 12,
      name: "12",
    },
  ];

  const columns = [
    {
      accessorKey: "mrt-row-numbers",
      header: "Sr No.",
      enableColumnOrdering: false,
      enableEditing: false,
      enableSorting: false,
      size: 80,
      Cell: ({ row }) => row.index + 1,
    },
    {
      header: "Name",
      accessorKey: "name",
      size: 200,
    },
    {
      header: "Interval",
      accessorKey: "interval",
    },
    {
      id: "actions",
      header: "Action",
      size: 120,
      Cell: ({ row }) => (
        <button
          type="button"
          className="btn p-0"
          onClick={() => handleEditClick(row.original.id)}
        >
          <i className="fas fa-pencil"></i>
        </button>
      ),
    },
  ];

  const exportToExcel = () => {
    const filteredData = feeCycles.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.name,
      Interval: row.interval,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "FeeCycles");
    XLSX.writeFile(workbook, "FeeCycles.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    // doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Fee Cycles List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const headers = [["Sr No", "Name", "Interval"]];

    const bodyData = feeCycles.map((row, index) => [
      index + 1,
      row.name,
      row.interval,
    ]);

    doc.autoTable({
      head: headers,
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("FeeCycles.pdf");
  };

  const handleEditClick = async (rowId) => {
    const singleFeeCycle = await get(
      `FeeCycle/GetFeeCycleByIdAsync?feeCycleId=${rowId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    const singleFeeCycleData = singleFeeCycle.feeCycle;

    reset({
      id: singleFeeCycleData.id,
      name: singleFeeCycleData.name,
      interval: singleFeeCycleData.interval,
      organizationId: singleFeeCycleData.organizationId,
      code: singleFeeCycleData.code,
      createdBy: singleFeeCycleData.createdBy,
      updatedBy: localStorage.getItem("userId"),
    });
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await post("FeeCycle/AddUpdateFeeCycleAsync", data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        reset({
          id: 0,
          name: "",
          interval: "",
          createdBy: localStorage.getItem("userId"),
          organizationId: organizationId,
        });
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Fee Cycle</h2>
      </div>

      <form className="row g-3 " onSubmit={handleSubmit(saveData)}>
        <div className="col-md-6 new-form-style">
          <div className="card">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Name<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`name`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("name")}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name.message}</small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Interval<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="interval"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <select
                        {...field}
                        className="form-select"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      >
                        <option value="" disabled key={"interval-key"}>
                          Select
                        </option>
                        {intervals.map((interval) => (
                          <option key={interval.id} value={interval.id}>
                            {interval.name}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.interval && (
                    <small className="text-danger">
                      {errors.interval.message}
                    </small>
                  )}
                </div>
                <div className="col-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={feeCycles}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{ columnPinning: { right: ["actions"] } }}
              />
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default FeeCycle;
